import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';

function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, signup } = useUser();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Error state
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formError, setFormError] = useState('');
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Refs
  const emailInputRef = useRef<HTMLInputElement>(null);
  
  // Set meta tags
  useMetaTags(
    t('auth.signup', 'Signup'),
    t('auth.signupDescription', 'Create an account to start using our application')
  );
  
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);
  
  // Focus email input on load
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);
  
  // Validate password
  const validatePassword = (pass: string): boolean => {
    // Minimum length check
    if (pass.length < 8) {
      setPasswordError(t('auth.passwordTooShort', 'Password must be at least 8 characters long'));
      return false;
    }
    
    // Reset error
    setPasswordError('');
    return true;
  };
  
  // Validate email
  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailValue || !emailRegex.test(emailValue)) {
      setEmailError(t('auth.invalidEmail', 'Please enter a valid email address'));
      return false;
    }
    
    // Reset error
    setEmailError('');
    return true;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setFormError('');
    
    // Validate all fields
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    
    if (!isEmailValid || !isPasswordValid) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      const success = await signup(email, password);
      
      if (success) {
        navigate('/');
      } else {
        setFormError(t('auth.signupFailed', 'Signup failed. Please try again.'));
      }
    } catch (error) {
      console.error('Signup error:', error);
      setFormError(t('auth.signupFailed', 'Signup failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h1 className="h4 mb-4 text-center">{t('auth.createAccount', 'Create an Account')}</h1>
              
              {/* Value proposition */}
              <div className="alert alert-info mb-4">
                <h5 className="h6">{t('auth.whySignup', 'Why create an account?')}</h5>
                <ul className="mb-0 ps-3">
                  <li>{t('auth.benefit1', 'Create and manage your counters')}</li>
                  <li>{t('auth.benefit2', 'Access your counters from any device')}</li>
                  <li>{t('auth.benefit3', 'Free to start, upgrade anytime')}</li>
                </ul>
              </div>
              
              {formError && (
                <div className="alert alert-danger" role="alert">
                  {formError}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* Email */}
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    {t('auth.email', 'Email')}
                  </label>
                  <input
                    ref={emailInputRef}
                    type="email"
                    className={`form-control ${emailError ? 'is-invalid' : ''}`}
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                  {emailError && (
                    <div className="invalid-feedback">{emailError}</div>
                  )}
                </div>
                
                {/* Password */}
                <div className="mb-4">
                  <label htmlFor="password" className="form-label">
                    {t('auth.password', 'Password')}
                  </label>
                  <input
                    type="password"
                    className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.passwordPlaceholder', 'Min. 8 characters')}
                    required
                    minLength={8}
                  />
                  {passwordError && (
                    <div className="invalid-feedback">{passwordError}</div>
                  )}
                  {password && <PasswordStrengthMeter password={password} />}
                </div>
                
                {/* Submit Button */}
                <div className="d-grid">
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('common.loading', 'Loading...')}
                      </>
                    ) : (
                      t('auth.createAccount', 'Create Account')
                    )}
                  </button>
                </div>
              </form>
              
              {/* Login Link */}
              <div className="mt-4 text-center">
                <span>{t('auth.alreadyHaveAccount', 'Already have an account?')} </span>
                <Link to="/login" className="text-primary">
                  {t('auth.login', 'Login')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup; 